import React, { useRef, useState } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
} from '@mui/material';
import { Person } from '@mui/icons-material';
import { IUser } from '../Interfaces/IUser';
import { useGetUsers } from '../hooks/data';

interface IProps {
  value: string;
  /** Per @ erwähnte Benutzer — wird bei jeder Textänderung mitgepflegt. */
  mentionedUserIds: number[];
  onChange: (value: string, mentionedUserIds: number[]) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  minRows?: number;
  /** Wo die Vorschlagsliste erscheint:
   * - 'bottom'/'top': schwebend unter/über dem Feld (für Dialoge mit Platz)
   * - 'inline': im Textfluss, das umgebende Element (z.B. eine Tabellenzeile)
   *   wird dadurch höher — kein Abschneiden in Containern mit overflow.
   * Default 'bottom'. */
  suggestionsPosition?: 'bottom' | 'top' | 'inline';
}

const MENTION_QUERY_REGEX = /@([\wäöüÄÖÜß]*)$/;

/**
 * Mehrzeiliges Textfeld mit @-Mentions (#1190): `@` öffnet eine Liste der
 * aktiven Benutzer; die Auswahl fügt `@Vorname Nachname` ein und merkt sich
 * die idUser in mentionedUserIds. Wird eine Erwähnung wieder aus dem Text
 * gelöscht, fliegt die id automatisch raus. Wiederverwendbar (Aufgaben-
 * Kommentare, später Vorgangs-Notizen).
 */
export const MentionTextField: React.FC<IProps> = ({
  value,
  mentionedUserIds,
  onChange,
  label,
  placeholder,
  disabled = false,
  minRows = 2,
  suggestionsPosition = 'bottom',
}) => {
  const { users } = useGetUsers();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);

  const activeUsers = (users ?? []).filter(user => !user.isBlocked);
  const nameOf = (user: IUser) => `${user.FirstName} ${user.LastName}`;

  const recomputeMentions = (text: string, ids: number[]) =>
    Array.from(new Set(ids)).filter(id => {
      const user = activeUsers.find(u => u.idUser === id);
      return user !== undefined && text.includes(`@${nameOf(user)}`);
    });

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = event.target.value;
    const cursor = event.target.selectionStart ?? text.length;
    const match = text.slice(0, cursor).match(MENTION_QUERY_REGEX);
    setMentionQuery(match ? match[1] : null);
    onChange(text, recomputeMentions(text, mentionedUserIds));
  };

  const handleSelect = (user: IUser) => {
    const cursor = inputRef.current?.selectionStart ?? value.length;
    const beforeCursor = value.slice(0, cursor);
    const match = beforeCursor.match(MENTION_QUERY_REGEX);
    if (!match) {
      return;
    }
    const start = beforeCursor.length - match[0].length;
    const newText = `${value.slice(0, start)}@${nameOf(user)} ${value.slice(cursor)}`;
    setMentionQuery(null);
    onChange(
      newText,
      recomputeMentions(newText, [...mentionedUserIds, user.idUser])
    );
    inputRef.current?.focus();
  };

  const suggestions =
    mentionQuery === null
      ? []
      : activeUsers.filter(user =>
          nameOf(user).toLowerCase().includes(mentionQuery.toLowerCase())
        );

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <TextField
        fullWidth
        multiline
        size='small'
        minRows={minRows}
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onBlur={() => {
          // Verzögert schließen, damit ein Klick auf die Liste noch greift.
          setTimeout(() => setMentionQuery(null), 200);
        }}
        disabled={disabled}
        inputRef={inputRef}
      />
      {mentionQuery !== null && suggestions.length > 0 && (
        <Paper
          elevation={suggestionsPosition === 'inline' ? 0 : 4}
          variant={suggestionsPosition === 'inline' ? 'outlined' : 'elevation'}
          sx={{
            maxHeight: 200,
            overflowY: 'auto',
            ...(suggestionsPosition === 'inline'
              ? { position: 'static', mt: 0.5 }
              : {
                  position: 'absolute',
                  zIndex: theme => theme.zIndex.modal + 1,
                  left: 0,
                  right: 0,
                  ...(suggestionsPosition === 'top'
                    ? { bottom: '100%', mb: 0.5 }
                    : { top: '100%', mt: 0.5 }),
                }),
          }}
        >
          <List dense>
            {suggestions.map(user => (
              <ListItemButton
                key={`mention-user-${user.idUser}`}
                onMouseDown={event => {
                  event.preventDefault();
                  handleSelect(user);
                }}
              >
                <Person fontSize='small' sx={{ mr: 1 }} />
                <ListItemText primary={nameOf(user)} />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};
